
import Component from "vue-class-component";
import Vue from "vue";
import {
    AddVtsCenterPracticeRequest,
    VtsCenterPracticeActions,
    PracticeKind,
    PositionOption,
    OrganizationOption,
    PositionsClient,
    PracticesClient,
    PracticePeriod,
    ResultError
} from "@/api-client";
import PracticePeriods from "@/features/documents/practices/components/PracticePeriods.vue";
import {Prop} from "vue-property-decorator";
import Loader from "@/features/shared/components/Loader";
import toasts from "@/components/Toasts";
import userInfo from "@/features/shared/components/UserInfo";
import LabelOptional from "@/features/shared/components/LabelOptional.vue";
import CheckboxInput from "@/features/shared/components/CheckboxInput.vue";
import FileUploader from "@/components/FileUploader.vue";
import Files from "@/components/Files.vue";
import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";

@Component({
    components: {
        CheckboxInput,
        Files,
        FileUploader,
        HeadingGroup,
        LabelOptional,
        PracticePeriods,
    }
})
export default class VtsCenterPracticeUpsertForm extends Vue {
    @Prop({required: true})
    seafarerId!: string;

    @Prop({required: true})
    model!: AddVtsCenterPracticeRequest;

    @Prop({required: true})
    actions!: VtsCenterPracticeActions;

    @Prop({required: true})
    submitCallback!: (model: AddVtsCenterPracticeRequest) => Promise<void>;

    validationErrors = ResultError.createEmpty();

    warningMatching = false;

    isOrganizationEnteredManually = false;

    positionOptions: PositionOption[] = [];
    organizationOptions: OrganizationOption[] = [];

    fileLoader = new Loader();
    submitLoader = new Loader();
    isCreated = false;
    isSeafarer = userInfo.seafarerId === this.seafarerId;

    async created() {
        await Promise.all(
            [
                new PositionsClient().activePositionOptions().then(x => this.positionOptions = x),
                new PracticesClient().vtsCenterOrganizationOptions().then(x => {
                    this.organizationOptions = x.organizationOptions.filter(o => o.active);
                }),
            ]
        );

        // VTS Center practice is always Work type
        this.model.kind = PracticeKind.Work;

        // Initialize practice periods if not present
        if (!this.model.practicePeriods) {
            this.model.practicePeriods = [];
        }

        // Check if organizationId exists to determine if it was selected from dropdown
        // If organizationId is null but organizationName exists, it was manually entered
        if (this.model.organizationId) {
            this.isOrganizationEnteredManually = false;
        } else if (this.model.organizationName) {
            this.isOrganizationEnteredManually = true;
        }

        // Deep watch the model to ensure nested arrays like practicePeriods are reactive
        this.$watch(() => this.model, () => {
            // This deep watcher ensures Vue tracks changes to practicePeriods array
        }, {deep: true});

        this.$watch(
            () => this.isOrganizationEnteredManually,
            () => {
                if (this.isOrganizationEnteredManually) {
                    this.model.organizationId = null;
                } else {
                    this.model.organizationName = null;
                }
            }
        );

        this.isCreated = true;
    }

    get canSubmit() {
        return this.validationErrors.isEmpty && !this.submitLoader.loading;
    }

    private addPracticePeriod(period: PracticePeriod) {
        this.model.practicePeriods.push(period);
    }

    private removePracticePeriod(practicePeriod: PracticePeriod) {
        this.$delete(
            this.model.practicePeriods,
            this.model.practicePeriods.indexOf(practicePeriod)
        );
    }

    private async submit() {
        if (!this.canSubmit) {
            return;
        }

        await this.submitLoader.startLoading();
        this.validationErrors.clear();
        
        // Set issuedOn to today's date if not already set (VTS practice doesn't have this field in UI)
        // This ensures the backend doesn't receive null for this required field
        if (!this.model.issuedOn) {
            this.model.issuedOn = new Date();
        }
        
        await this.submitCallback(this.model)
            .then(async () => {
                toasts.success(this.$ls.Documents_Qualifications_Toast_Success_DocumentSaving);
            })
            .catch((error: ResultError) => {
                this.validationErrors = error;
                toasts.failure(this.$ls.Global_Toast_FormSaveFailure);
            })
        this.submitLoader.endLoading();
    }
}
